import Button, { ButtonProps } from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import { Theme } from '@material-ui/core/styles/createMuiTheme';
import createStyles from '@material-ui/core/styles/createStyles';
import withStyles, { WithStyles } from '@material-ui/core/styles/withStyles';
import { AllVariants } from '@serko/zui/es/style/typography';
import Typography from '@serko/zui/es/Typography';
import classNames from 'classnames';
import React, { cloneElement, Children, PureComponent, ReactNode } from 'react';

type IconSize = 'sm' | 'md' | 'lg';
type HeadingSize = 'sm' | 'lg';

interface OwnProps {
  icon?: JSX.Element;
  heading?: ReactNode;
  message?: ReactNode;
  PrimaryActionProps?: ButtonProps;
  SecondaryActionProps?: ButtonProps;
  iconSize?: IconSize;
  headingSize?: HeadingSize;
}

interface EmptyStateProps extends OwnProps, WithStyles<typeof styles> {}

class EmptyStateBase extends PureComponent<EmptyStateProps> {
  public render() {
    const {
      classes,
      icon,
      message,
      heading,
      PrimaryActionProps,
      SecondaryActionProps,
      iconSize,
      headingSize,
    } = this.props;

    const iconSizeClassName = (): string => {
      switch (iconSize) {
        case 'sm':
          return classes.iconSizeSmall;
        case 'md':
          return classes.iconSizeMedium;
        case 'lg':
          return classes.iconSizeLarge;
        default:
          return classes.iconSizeSmall;
      }
    };

    const headingSizeVariant = (): AllVariants => {
      switch (headingSize) {
        case 'lg':
          return 'title';
        case 'sm':
        default:
          return 'subheading';
      }
    };

    return (
      <Grid
        container
        className={classes.root}
        alignItems="center"
        justify="space-around"
      >
        {!!icon && (
          <Grid item xs={12} className={classes.iconWrap}>
            {cloneElement(icon, {
              className: classNames(
                Children.only(icon).props.className,
                classes.icon,
                iconSizeClassName(),
              ),
            })}
          </Grid>
        )}
        {!!heading && (
          <Grid item xs={12} className={classes.headingWrap}>
            <Typography
              variant={headingSizeVariant()}
              color="textSecondary"
              align="center"
            >
              {heading}
            </Typography>
          </Grid>
        )}
        {!!message && (
          <Grid item xs={12} className={classes.messageWrap}>
            <Typography variant="body1" color="textSecondary" align="center">
              {message}
            </Typography>
          </Grid>
        )}
        {!!PrimaryActionProps && (
          <Grid item xs={12} className={classes.primaryActionWrap}>
            <Button
              color="primary"
              variant="contained"
              {...PrimaryActionProps}
            />
          </Grid>
        )}
        {!!SecondaryActionProps && (
          <Grid item xs={12} className={classes.secondaryActionWrap}>
            <Button color="primary" variant="text" {...SecondaryActionProps} />
          </Grid>
        )}
      </Grid>
    );
  }
}

// styled
const styles = (theme: Theme) =>
  createStyles({
    root: { padding: theme.spacing.unit * 2 },
    icon: {
      color: theme.zeno.icons.dark.unavailable,
    },
    iconWrap: {
      marginBottom: theme.spacing.unit,
      display: 'flex',
      justifyContent: 'center',
    },
    headingWrap: {
      marginBottom: theme.spacing.unit,
    },
    messageWrap: {},
    primaryActionWrap: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: theme.spacing.unit,
    },
    secondaryActionWrap: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: theme.spacing.unit,
    },
    iconSizeSmall: {
      fontSize: 60,
    },
    iconSizeMedium: {
      fontSize: 90,
    },
    iconSizeLarge: {
      fontSize: 120,
    },
  });

export const EmptyState = withStyles(styles)<OwnProps>(EmptyStateBase);
